import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  useAvailableServiceAreas,
  useCarrierTransportServices,
  useDeclareServiceArea,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import {
  parseOptionalCapacity,
  serviceCoverageDrafts,
  serviceCoverageInputs,
  type ServiceCoverageDraft,
  ServiceCoverageFields,
  validateServiceCoverages,
} from "@/features/service-area-form-controls";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { hasHttpStatus } from "@/lib/api-error";

interface DeclareServiceAreaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeclareServiceAreaDialog({
  open,
  onOpenChange,
}: DeclareServiceAreaDialogProps) {
  const available = useAvailableServiceAreas();
  const transportServices = useCarrierTransportServices();
  const services = (transportServices.data ?? []).filter(
    (service) => service.isActive
  );
  const declare = useDeclareServiceArea();
  const [serviceAreaId, setServiceAreaId] = useState("");
  const [serviceCoverage, setServiceCoverage] = useState<
    Record<string, ServiceCoverageDraft>
  >({});
  const [maxDaily, setMaxDaily] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const pending = submitting || declare.isPending;
  const selectable = (available.data ?? []).filter(
    (area) => area.assignment?.active !== true
  );
  const selected = selectable.find((area) => area.id === serviceAreaId);
  const reactivating = selected?.assignment != null;
  const nothingLeft =
    !available.isLoading && !available.error && selectable.length === 0;
  const dirty =
    serviceAreaId !== "" ||
    Object.keys(serviceCoverage).length > 0 ||
    maxDaily !== "";
  const formId = "declare-service-area-form";

  useUnsavedChangesGuard(
    open && dirty,
    "Leaving this page will discard this unsaved service area. Continue?"
  );

  useEffect(() => {
    if (!open) return;
    setServiceAreaId("");
    setServiceCoverage({});
    setMaxDaily("");
    setError(null);
  }, [open]);

  const close = () => {
    if (pending) return;
    if (dirty && !window.confirm("Discard this unsaved service area?")) return;
    onOpenChange(false);
  };

  const selectArea = (id: string) => {
    setServiceAreaId(id);
    setError(null);
    const area = selectable.find((candidate) => candidate.id === id);
    if (area?.assignment) {
      setServiceCoverage(
        serviceCoverageDrafts(area.assignment.serviceCoverages)
      );
      setMaxDaily(
        area.assignment.maxDailyOrders == null
          ? ""
          : String(area.assignment.maxDailyOrders)
      );
      return;
    }
    setServiceCoverage({});
    setMaxDaily("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submissionLock.current) return;
    setError(null);
    if (!serviceAreaId) {
      setError("Choose a service area.");
      return;
    }
    const capacity = parseOptionalCapacity(maxDaily);
    if (capacity.error) {
      setError(capacity.error);
      return;
    }
    const coverageError = validateServiceCoverages(services, serviceCoverage);
    if (coverageError) {
      setError(coverageError);
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    try {
      await declare.mutateAsync({
        serviceAreaId,
        serviceCoverages: serviceCoverageInputs(services, serviceCoverage),
        maxDailyOrders: capacity.value,
      });
      toast.success(
        reactivating ? "Service area reactivated." : "Service area added."
      );
      onOpenChange(false);
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to add service areas."
        : (response.response?.data?.error ??
          "Service area could not be added.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      title="Add service area"
      presentation="responsive-sheet"
      closeDisabled={pending}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              onClick={close}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              size="touch"
              className="w-full sm:w-auto"
              disabled={
                pending ||
                nothingLeft ||
                !!available.error ||
                transportServices.isLoading
              }
            >
              {pending
                ? "Adding…"
                : reactivating
                  ? "Reactivate area"
                  : "Add area"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-busy={pending || undefined}
      >
        <p className="text-sm text-muted-foreground">
          Pick an EZX-defined geographic area you deliver to. Your services can
          only appear in quotes for areas you cover.
        </p>

        {available.isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading available areas…
          </p>
        ) : available.error ? (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>
              {hasHttpStatus(available.error, 403)
                ? "You do not have permission to view available service areas."
                : "Available service areas could not be loaded."}
            </p>
            {!hasHttpStatus(available.error, 403) && (
              <Button
                type="button"
                variant="outline"
                size="touch"
                onClick={() => void available.refetch()}
              >
                Try again
              </Button>
            )}
          </div>
        ) : (
          <label htmlFor="sa-area" className="space-y-1 text-sm font-medium">
            <span>Area</span>
            <NativeSelect
              id="sa-area"
              aria-label="Area"
              value={serviceAreaId}
              onChange={(event) => selectArea(event.target.value)}
              disabled={pending || nothingLeft}
            >
              <option value="">Select an area…</option>
              {selectable.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                  {area.assignment ? " (currently off)" : ""}
                </option>
              ))}
            </NativeSelect>
          </label>
        )}

        {nothingLeft && (
          <p role="status" className="text-sm text-muted-foreground">
            You already cover every area EZX serves.
          </p>
        )}
        {reactivating && (
          <p className="text-sm text-muted-foreground">
            Reactivating restores the area’s saved services and capacity.
          </p>
        )}

        <ServiceCoverageFields
          services={services}
          values={serviceCoverage}
          savedValues={
            selected?.assignment
              ? serviceCoverageDrafts(selected.assignment.serviceCoverages)
              : {}
          }
          areaId={serviceAreaId}
          disabled={pending || transportServices.isLoading}
          onChange={(serviceId, value) => {
            setServiceCoverage((current) => ({
              ...current,
              [serviceId]: value,
            }));
            setError(null);
          }}
        />

        <label htmlFor="sa-new-cap" className="space-y-1 text-sm font-medium">
          <span>Max daily orders (blank = platform default)</span>
          <Input
            id="sa-new-cap"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={maxDaily}
            onChange={(event) => setMaxDaily(event.target.value)}
            disabled={pending}
            className="w-full sm:w-48"
          />
        </label>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
